import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { listYoutubeSources, addYoutubeQuery, deleteYoutubeSource, YoutubeSourceRow } from '../../services/onboarding';
import { ChannelIcon } from './channelIcons';

const input = 'flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500';
const ghost = 'inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-lg disabled:opacity-50';

export default function YouTubeSourcesCard() {
  const [rows, setRows] = useState<YoutubeSourceRow[]>([]);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = () => listYoutubeSources().then(setRows).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const have = new Set(rows.map(r => r.query.toLowerCase()));

  const add = async (q: string) => {
    const clean = q.trim();
    if (clean.length < 2 || have.has(clean.toLowerCase())) return;
    setBusy(true);
    try { await addYoutubeQuery(clean); await refresh(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };

  const remove = async (id: string) => { await deleteYoutubeSource(id); refresh(); };

  return (
    <div className="border border-gray-100 rounded-xl p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <ChannelIcon id="youtube" className="w-4 h-4" /> YouTube search queries
        </h3>
        <p className="text-xs text-gray-500">
          We search videos for each query and scan their comments for prospects. Empty = YouTube is skipped.
        </p>
      </div>

      <div className="flex gap-2 mb-3">
        <input className={input} placeholder='e.g. "moving to dubai apartment"' value={val}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add(val), setVal(''))} />
        <button onClick={() => { add(val); setVal(''); }} disabled={busy || val.trim().length < 2} className={ghost}>Add</button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {rows.map(r => (
          <span key={r.id} className="inline-flex items-center gap-1.5 text-xs font-medium bg-gray-50 border border-gray-200 text-gray-700 px-2.5 py-1 rounded-lg">
            {r.query}
            <button onClick={() => remove(r.id)} className="text-gray-400 hover:text-red-500"><XMarkIcon className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {!rows.length && <p className="text-xs text-gray-400">No queries yet — add a search phrase your customers would relate to.</p>}
      </div>
    </div>
  );
}
